// ---------------------------------------------------------------------------
// FA Invite — data model (demo front-end only, no backend)
// ---------------------------------------------------------------------------

export type FaAvailability = 'available' | 'busy' | 'offline';

export const FA_SPECIALTIES = [
  'Debt',
  'Housing',
  'Tax',
  'Benefits',
  'Budgeting',
  'Emergency Cash',
  'Transportation',
  'Legal Referrals',
] as const;

export type FaSpecialty = (typeof FA_SPECIALTIES)[number];

export interface FaProfile {
  id: string;
  name: string;
  email: string;
  isCfp: boolean;
  tenureMonths: number;
  specialties: FaSpecialty[];
  availability: FaAvailability;
  employerId: string;
  avatarInitials: string;
}

export type InviteStatus = 'none' | 'pending' | 'accepted' | 'declined';

export interface FaInvite {
  inviteeId: string;
  status: InviteStatus;
  reason?: string;
  sentAt: string;
  resolvedAt?: string;
}

/** Per-session invite state. */
export interface FaInviteSessionState {
  /** Current invite for this conversation, if any. */
  invite: FaInvite | null;
  /** FA profiles that have joined (accepted) and can post messages. */
  joinedFaIds: string[];
}

// ---------------------------------------------------------------------------
// Mock FA roster (same employer, 8 profiles)
// ---------------------------------------------------------------------------

export const MOCK_FA_PROFILES: FaProfile[] = [
  {
    id: 'fa-001',
    name: 'Sarah Chen',
    email: 'sarah.chen@brightside.com',
    isCfp: true,
    tenureMonths: 32,
    specialties: ['Debt', 'Budgeting', 'Tax'],
    availability: 'available',
    employerId: 'employer-brightside',
    avatarInitials: 'SC',
  },
  {
    id: 'fa-002',
    name: 'Marcus Webb',
    email: 'marcus.webb@brightside.com',
    isCfp: false,
    tenureMonths: 14,
    specialties: ['Housing', 'Emergency Cash'],
    availability: 'available',
    employerId: 'employer-brightside',
    avatarInitials: 'MW',
  },
  {
    id: 'fa-003',
    name: 'Jordan Lee',
    email: 'jordan.lee@brightside.com',
    isCfp: true,
    tenureMonths: 56,
    specialties: ['Benefits', 'Tax', 'Legal Referrals'],
    availability: 'busy',
    employerId: 'employer-brightside',
    avatarInitials: 'JL',
  },
  {
    id: 'fa-004',
    name: 'Alex Rivera',
    email: 'alex.rivera@brightside.com',
    isCfp: false,
    tenureMonths: 8,
    specialties: ['Transportation', 'Emergency Cash'],
    availability: 'available',
    employerId: 'employer-brightside',
    avatarInitials: 'AR',
  },
  {
    id: 'fa-005',
    name: 'Priya Nair',
    email: 'priya.nair@brightside.com',
    isCfp: true,
    tenureMonths: 44,
    specialties: ['Debt', 'Housing', 'Budgeting'],
    availability: 'offline',
    employerId: 'employer-brightside',
    avatarInitials: 'PN',
  },
  {
    id: 'fa-006',
    name: 'Devon Carter',
    email: 'devon.carter@brightside.com',
    isCfp: false,
    tenureMonths: 20,
    specialties: ['Benefits', 'Emergency Cash'],
    availability: 'busy',
    employerId: 'employer-brightside',
    avatarInitials: 'DC',
  },
  {
    id: 'fa-007',
    name: 'Mia Torres',
    email: 'mia.torres@brightside.com',
    isCfp: true,
    tenureMonths: 68,
    specialties: ['Tax', 'Legal Referrals', 'Debt'],
    availability: 'available',
    employerId: 'employer-brightside',
    avatarInitials: 'MT',
  },
  {
    id: 'fa-008',
    name: 'Liam Park',
    email: 'liam.park@brightside.com',
    isCfp: false,
    tenureMonths: 5,
    specialties: ['Budgeting', 'Transportation'],
    availability: 'offline',
    employerId: 'employer-brightside',
    avatarInitials: 'LP',
  },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

export function formatTenure(months: number): string {
  const years = Math.floor(months / 12);
  const rem = months % 12;
  if (years === 0) return `${rem}m at Brightside`;
  if (rem === 0) return `${years}y at Brightside`;
  return `${years}y ${rem}m at Brightside`;
}

/** Default sort: available first, then busy, then offline; alpha within group. */
export function sortFaProfiles(profiles: FaProfile[]): FaProfile[] {
  const order: Record<FaAvailability, number> = { available: 0, busy: 1, offline: 2 };
  return [...profiles].sort((a, b) => {
    const diff = order[a.availability] - order[b.availability];
    return diff !== 0 ? diff : a.name.localeCompare(b.name);
  });
}

export function filterFaProfiles(profiles: FaProfile[], query: string): FaProfile[] {
  const q = query.trim().toLowerCase();
  if (!q) return profiles;
  return profiles.filter(
    (p) => p.name.toLowerCase().includes(q) || p.email.toLowerCase().includes(q)
  );
}
